/**
 * Tag-rule threshold overrides — the one place precedence is decided.
 * For Media Devoted | Created By Ciro Autiero
 *
 * A metric condition ("REG >= 30") carries a default `value` and may carry
 * more specific thresholds:
 *
 *   campaign override  →  country override  →  default value
 *
 * Campaign beats country because it is strictly narrower: a country holds many
 * campaigns, and one of them behaving differently should not force the whole
 * country onto a different number.
 *
 *   REG >= 30                          (default)
 *     country  australia      -> 50
 *     campaign AU-Casino-SMS  -> 80
 *
 *   AU-Casino-SMS      -> 80   (campaign wins)
 *   other AU campaign  -> 50   (country)
 *   anything else      -> 30   (default)
 *
 * TWO evaluators consume this — the Flow Optimizer ranking tags and the
 * SMSWiser performance tags — and the FO one runs again in the browser to draw
 * the live preview chips. All three MUST delegate here; a reimplementation is
 * how the chip and the rotation end up disagreeing.
 */

import type { TagCompareTo } from "../flow-optimizer-compare-to";

/** Tracker a campaign belongs to. Matches the `smswiser_traffic_provider` enum. */
export type TagTrafficProvider = "VOLUUM" | "BINOM" | "TRES_COMMAS";

/**
 * A per-campaign threshold. Keyed by (provider, campaignId) — never the id
 * alone: Voluum ids are UUIDs and Binom ids are small integers, so a bare "1"
 * is ambiguous across trackers. Mirrors `campaign_static_tag`'s
 * `@@unique([provider, campaignId])`.
 */
export interface TagCampaignThreshold {
  provider: TagTrafficProvider;
  campaignId: string;
  value: number;
  /** When set, this scope COMPARES against another metric instead of pinning
   *  `value` (which is then ignored). Same contract as a condition's own
   *  compareTo — window inherit = the rule's window. */
  compareTo?: TagCompareTo;
}

/** A per-country threshold (the pre-existing override kind). */
export interface TagCountryThreshold {
  countryKey: string;
  value: number;
  /** See {@link TagCampaignThreshold.compareTo}. */
  compareTo?: TagCompareTo;
}

/**
 * What the evaluator knows about the row being tagged. Every field is optional:
 * an absent campaign simply skips that tier, which is exactly what the
 * country-wide roll-up needs (its metrics are blended across campaigns, so a
 * campaign-specific threshold has nothing to compare against).
 */
export interface TagOverrideContext {
  countryKey?: string;
  campaignId?: string;
  provider?: TagTrafficProvider;
}

const norm = (v: unknown): string => String(v ?? "").trim().toLowerCase();

const finite = (v: unknown): v is number =>
  typeof v === "number" && Number.isFinite(v);

/** A row is usable when it pins a finite number OR carries a comparison. */
const usable = (o: { value: number; compareTo?: TagCompareTo }): boolean =>
  o.compareTo !== undefined || finite(o.value);

/** Campaign ids are compared case-insensitively: Voluum stores UUIDs lowercase
 *  but a pasted id is often upper, and that should still match. */
function matchCampaignEntry(
  list: ReadonlyArray<TagCampaignThreshold> | undefined,
  ctx: TagOverrideContext,
): TagCampaignThreshold | undefined {
  const id = norm(ctx.campaignId);
  if (!id || !ctx.provider || !list?.length) return undefined;
  const provider = ctx.provider;
  const hit = list.find(
    (o) => o?.provider === provider && norm(o.campaignId) === id,
  );
  return hit && usable(hit) ? hit : undefined;
}

function matchCountryEntry(
  list: ReadonlyArray<TagCountryThreshold> | undefined,
  ctx: TagOverrideContext,
): TagCountryThreshold | undefined {
  const key = norm(ctx.countryKey);
  if (!key || !list?.length) return undefined;
  const hit = list.find((o) => norm(o?.countryKey) === key);
  return hit && usable(hit) ? hit : undefined;
}

export interface ResolveOverrideInput {
  /** The condition's default threshold. */
  value: number | string;
  /** The condition's default comparison, when it has one. */
  compareTo?: TagCompareTo;
  countryThresholds?: ReadonlyArray<TagCountryThreshold>;
  campaignThresholds?: ReadonlyArray<TagCampaignThreshold>;
  ctx: TagOverrideContext;
}

/** What the winning tier says the threshold IS: a number, or a comparison. */
export interface ResolvedOverrideEntry {
  tier: "campaign" | "country" | "default";
  /** Fixed threshold; meaningful only when `compareTo` is absent. */
  value: number;
  compareTo?: TagCompareTo;
}

/**
 * Resolve which ENTRY applies under `ctx` — campaign, else country, else the
 * condition's own value/compareTo. The winning entry decides the threshold
 * KIND: a row with `compareTo` compares (its `value` is ignored); a row
 * without pins a fixed number, replacing even a base comparison.
 */
export function resolveOverrideEntry(
  input: ResolveOverrideInput,
): ResolvedOverrideEntry {
  const byCampaign = matchCampaignEntry(input.campaignThresholds, input.ctx);
  if (byCampaign) {
    return {
      tier: "campaign",
      value: byCampaign.value,
      compareTo: byCampaign.compareTo,
    };
  }
  const byCountry = matchCountryEntry(input.countryThresholds, input.ctx);
  if (byCountry) {
    return {
      tier: "country",
      value: byCountry.value,
      compareTo: byCountry.compareTo,
    };
  }
  return {
    tier: "default",
    value: finite(input.value) ? input.value : Number(input.value),
    compareTo: input.compareTo,
  };
}

/**
 * Resolve the threshold to compare against. An override whose value is not a
 * finite number is discarded and the next tier is tried, so a corrupt row
 * degrades to the default rather than tagging everything or nothing.
 *
 * The returned value can still be NaN when the condition's own `value` is
 * unusable — callers guard with `Number.isFinite` before comparing (a NaN
 * threshold must never match).
 */
export function resolveOverrideThreshold(input: ResolveOverrideInput): number {
  const entry = resolveOverrideEntry(input);
  // A numeric-only caller cannot evaluate a comparison — NaN keeps it
  // fail-closed instead of comparing against a bogus number.
  return entry.compareTo ? Number.NaN : entry.value;
}

/** True when either override kind carries at least one entry. */
export function hasAnyOverride(c: {
  countryThresholds?: ReadonlyArray<TagCountryThreshold>;
  campaignThresholds?: ReadonlyArray<TagCampaignThreshold>;
}): boolean {
  return (
    (c.countryThresholds?.length ?? 0) > 0 ||
    (c.campaignThresholds?.length ?? 0) > 0
  );
}

/** Stable identity for a campaign override row (dedupe + React keys). */
export function campaignThresholdKey(
  provider: TagTrafficProvider,
  campaignId: string,
): string {
  return `${provider}:${norm(campaignId)}`;
}
